import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import { useHistoriesQuery } from './useHistoriesQuery'
import { useLawsuitOwnerQuery } from './useLawsuitOwnerQuery'

/**
 * Port of `app/histories/histories.html` + `app/histories/historiesController.js`
 * (AngularJS `historiesController`) to React/MUI, backed by
 * `useHistoriesQuery` (`./useHistoriesQuery.js`) in place of
 * `HistoriesService` + the controller's own `load(cursor)` recursion.
 *
 * Screen behavior ported 1:1:
 * - Two free-text inputs, "serialNumber" and "jobId" (`form.serialNumber` /
 *   `form.jobId` in the legacy scope) — no client-side validation existed
 *   on either field in the legacy template, so none is added here either.
 * - "Buscar" button (`loadHistories()`): clears the current list, then
 *   fetches every page for the given filters (pagination handled inside the
 *   hook, see its doc-comment).
 * - "Fensalir" button (`openFensalir()`): opens
 *   `http://fensalir.aurum.com.br:8000/atualizacao/report?serialNumber=<serialNumber>`
 *   in a new tab/window — ported verbatim, including using whatever is
 *   currently typed in the serial number field (no validation there either
 *   in the legacy controller).
 * - Loading indicator (`ng-if="data.loading"`, a Materialize indeterminate
 *   `.progress` bar) -> MUI `LinearProgress`, shown while the mutation is
 *   pending.
 * - Results table: `Data` (formatted) / `Descrição` columns, one row per
 *   `history` in `data.list` (`history.formattedDate` / `history.description`).
 * - Errors (`Materialize.toast(error.message || 'Erro na requisição', 5000)`)
 *   -> MUI `Snackbar` + `Alert`, matching the pattern already established by
 *   `client/src/modules/register/SettingsPage.jsx` (task 013) for toast-like
 *   feedback, since no global toast/notistack infra exists yet in the React
 *   app (same rationale documented there).
 *
 * Not ported: the `#fountainG` id and `.wrap-responsive`/Materialize-specific
 * classes are purely Materialize/Bootstrap 3 layout chrome with no
 * behavioral meaning — superseded by MUI's own layout primitives per the
 * plan's UI-kit decision (Materialize -> MUI is not a 1:1 visual port).
 */
const SHARING_TYPE_LABELS = {
  0: 'Público',
  1: 'Privado',
  2: 'Equipe',
}

function sharingTypeLabel(value) {
  if (value === null || value === undefined) return '-'
  return SHARING_TYPE_LABELS[value] ?? String(value)
}

function HistoriesPage() {
  const [serialNumber, setSerialNumber] = useState('')
  const [jobId, setJobId] = useState('')
  const [historicals, setHistoricals] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  const historiesQuery = useHistoriesQuery()

  const [tenantId, setTenantId] = useState('')
  const [lawsuitNumber, setLawsuitNumber] = useState('')
  const [caseInfo, setCaseInfo] = useState(null)
  const [ownerSubmitted, setOwnerSubmitted] = useState(false)
  const { getOwner, isLoading: isOwnerLoading } = useLawsuitOwnerQuery()

  async function handleSearchOwner() {
    setOwnerSubmitted(true)
    setCaseInfo(null)
    try {
      const data = await getOwner({ tenantId, lawsuitNumber })
      setCaseInfo(data ?? null)
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro na requisição' })
    }
  }

  function handleSearch() {
    setHistoricals([])
    historiesQuery.mutate(
      { serialNumber, jobId },
      {
        onSuccess: (data) => setHistoricals(data),
        onError: (error) => {
          // Ported from `Materialize.toast(error.message || 'Erro na requisição', 5000)`.
          setSnackbar({ open: true, message: error?.message || 'Erro na requisição' })
        },
      },
    )
  }

  function handleOpenFensalir() {
    window.open(`http://fensalir.aurum.com.br:8000/atualizacao/report?serialNumber=${serialNumber}`)
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  const isLoading = historiesQuery.isPending
  const canSearchOwner = Boolean(tenantId) && Boolean(lawsuitNumber)
  const showOwnerEmptyState = ownerSubmitted && !isOwnerLoading && !caseInfo

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Informações
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Tenant ID"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            fullWidth
          />
          <TextField
            label="Número do processo"
            value={lawsuitNumber}
            onChange={(event) => setLawsuitNumber(event.target.value)}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handleSearchOwner}
            disabled={isOwnerLoading || !canSearchOwner}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Buscar
          </Button>
        </Stack>

        {isOwnerLoading && <LinearProgress sx={{ mt: 2 }} />}

        {caseInfo && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={0.5}>
              <Typography variant="body1">
                <strong>Criador:</strong> {caseInfo.ownerName || '-'}
              </Typography>
              <Typography variant="body1">
                <strong>Responsável:</strong> {caseInfo.responsibleName || '-'}
              </Typography>
              <Typography variant="body1">
                <strong>JobID:</strong> {caseInfo.jobId || '-'}
              </Typography>
              <Typography variant="body1">
                <strong>Compartilhamento:</strong> {sharingTypeLabel(caseInfo.sharingType)}
              </Typography>
              <Typography variant="body1">
                <strong>Time:</strong> {caseInfo.teamName || '-'}
              </Typography>
            </Stack>
          </>
        )}

        {showOwnerEmptyState && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum processo encontrado.
          </Alert>
        )}
      </Paper>

      <Typography variant="h5" component="h2" gutterBottom>
        Históricos
      </Typography>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              id="serialNumber"
              label="Digite o número de série"
              value={serialNumber}
              onChange={(event) => setSerialNumber(event.target.value)}
              fullWidth
            />
            <TextField
              id="jobId"
              label="Digite o jobId"
              value={jobId}
              onChange={(event) => setJobId(event.target.value)}
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleSearch} disabled={isLoading}>
              Buscar
            </Button>
            <Button variant="outlined" onClick={handleOpenFensalir} disabled={isLoading}>
              Fensalir
            </Button>
          </Stack>

          {isLoading && <LinearProgress />}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 150 }}>Data</TableCell>
                  <TableCell>Descrição</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historicals.map((history, index) => (
                  <TableRow key={`${history.date}-${index}`}>
                    <TableCell>{history.formattedDate}</TableCell>
                    <TableCell>{history.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default HistoriesPage
